/**
 * Legal pages — Terms of Use / Risk Disclosure / Privacy Policy.
 *
 * Content comes from /api/legal/documents (placeholder text until lawyer-
 * approved copy is provided). All three pages share the same shell so the
 * footer links can hand off to a single component.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const DOC_KEY_BY_PATH = {
  "/legal/terms": { key: "terms_of_use", title: "Terms of Use" },
  "/legal/risk": { key: "risk_disclosure", title: "Risk Disclosure" },
  "/legal/risk-disclosure": { key: "risk_disclosure", title: "Risk Disclosure" },
  "/legal/privacy": { key: "privacy_policy", title: "Privacy Policy" },
  "/legal/refund": { key: "refund_policy", title: "Refund Policy" },
};

const INLINE_RULES = [
  { re: /\*\*([^*]+)\*\*/g, tag: "strong" },
  { re: /(^|[\s(])_([^_]+)_(?=[\s.,;:)]|$)/g, tag: "em", keepPrefix: true },
];

function renderInline(text, key) {
  let parts = [text];
  INLINE_RULES.forEach(({ re, tag, keepPrefix }) => {
    parts = parts.flatMap((p) => {
      if (typeof p !== "string") return [p];
      const out = [];
      let last = 0, m;
      re.lastIndex = 0;
      while ((m = re.exec(p)) !== null) {
        const pre = keepPrefix ? m[1] : "";
        const inner = keepPrefix ? m[2] : m[1];
        out.push(p.slice(last, m.index) + pre);
        out.push(tag === "strong"
          ? <strong key={`${key}-${m.index}`} className="font-semibold" style={{ color: "var(--hoip-text-primary)" }}>{inner}</strong>
          : <em key={`${key}-${m.index}`}>{inner}</em>);
        last = m.index + m[0].length;
      }
      out.push(p.slice(last));
      return out;
    });
  });
  return parts;
}

function MarkdownDoc({ md }) {
  const blocks = [];
  const lines = md.split("\n");
  let para = [], list = [], k = 0;
  const flushPara = () => {
    if (para.length) {
      blocks.push(<p key={k++} className="text-[13px] leading-relaxed mb-3" style={{ color: "var(--hoip-text-secondary)" }}>{renderInline(para.join(" "), k)}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ul key={k++} className="list-disc pl-5 mb-3 space-y-1.5">
          {list.map((item, i) => (
            <li key={i} className="text-[13px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>{renderInline(item, `${k}-${i}`)}</li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (/^# /.test(line)) {
      flushPara(); flushList();
      // Top-level doc title is already shown by the page header — render as lede
      blocks.push(<h2 key={k++} className="text-lg font-bold mt-2 mb-3" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>{line.slice(2)}</h2>);
    } else if (/^## /.test(line)) {
      flushPara(); flushList();
      blocks.push(<h3 key={k++} className="text-[15px] font-bold mt-6 mb-2" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>{line.slice(3)}</h3>);
    } else if (/^### /.test(line)) {
      flushPara(); flushList();
      blocks.push(<h4 key={k++} className="text-[13px] font-bold mt-4 mb-1.5" style={{ color: "var(--hoip-text-primary)" }}>{line.slice(4)}</h4>);
    } else if (/^- /.test(line)) {
      flushPara();
      list.push(line.slice(2));
    } else if (line.trim() === "") {
      flushPara(); flushList();
    } else if (/^_.*_$/.test(line.trim())) {
      flushPara(); flushList();
      blocks.push(<p key={k++} className="text-[11px] italic opacity-60 mb-3">{line.trim().slice(1, -1)}</p>);
    } else {
      if (list.length && /^\s{2,}/.test(raw)) {
        list[list.length - 1] += " " + line.trim();
      } else {
        para.push(line.trim());
      }
    }
  });
  flushPara(); flushList();
  return <div className="mt-6" data-testid="legal-content">{blocks}</div>;
}

export default function LegalPage() {
  const location = useLocation();
  const cfg = DOC_KEY_BY_PATH[location.pathname] || DOC_KEY_BY_PATH["/legal/terms"];
  const [doc, setDoc] = useState(null);
  const [version, setVersion] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");

  useEffect(() => {
    api.get("/legal/documents")
      .then(({ data }) => {
        setDoc(data[cfg.key] || "");
        setVersion(data.version || "");
        setLastUpdated(data.last_updated || "");
      })
      .catch(() => setDoc("Unable to load this document. Please try again later."));
  }, [cfg.key]);

  return (
    <div className="min-h-screen px-6 py-10" style={{ background: "var(--hoip-bg)", color: "var(--hoip-text-primary)" }}>
      <div className="max-w-3xl mx-auto" data-testid={`legal-page-${cfg.key}`}>
        <a href="/" className="text-[11px] font-bold tracking-wide opacity-70 hover:opacity-100"
          style={{ color: "var(--hoip-text-secondary)" }}>← MarkovLab</a>
        <h1 className="mt-4 text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>{cfg.title}</h1>
        <div className="text-[11px] opacity-60 mt-1" data-testid="legal-version">
          Version: {version || "—"} · Last updated: {lastUpdated || "—"}
        </div>
        {doc == null ? (
          <p className="mt-6 text-[13px] opacity-60">Loading…</p>
        ) : (
          <MarkdownDoc md={doc} />
        )}
        <div className="mt-10 pt-6 border-t text-[11px] opacity-60"
          style={{ borderColor: "var(--hoip-border)" }}>
          For educational and informational purposes only. Not investment advice.
          Options involve substantial risk and are not suitable for all investors.
        </div>
      </div>
    </div>
  );
}
